import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import { applicationsApi } from '../store/api/applicationsApi';
import { routesApi, useGetAllRoutesQuery } from '../store/api/routesApi';
import type { Application, Route } from '../types';
import type { RouteViewModel } from '../types/routeViewModel';
import { formatDate } from '../utils/applicationDisplayFormat';
import { ENTITY_CARD_DASH } from '../utils/entityCardRouteUtils';

export type CarrierPenaltyTableRow = {
  key: string;
  routeId: number;
  routeNumber: string;
  reason: string;
  amount: number;
  status: string;
};

/** Одна строка таблицы маршрутов на вкладке «Штрафы» (данные из GET /routes/:id/view). */
export type CarrierRoutePenaltiesRow = {
  key: string;
  routeId: number;
  routeNumber: string;
  directionLabel: string;
  loadingDateDisplay: string;
  unloadingDateDisplay: string;
  clientName: string;
  clientId: number | null;
  /** Сырой статус маршрута (ROUTE) для StatusTag */
  routeStatus: string;
  isCompanyRoute: boolean;
  carrierContractRequestNumber: string | null;
  carrierContractRequestDate: string | null;
  penalties: CarrierPenaltyTableRow[];
  penaltyCount: number;
  penaltySum: number;
};

/** Заявки по маршрутам перевозчика (сводка из view + GET /applications/:id). */
export type CarrierApplicationRow = {
  key: string;
  applicationId: number;
  application: Application | null;
  internalNumber: string;
  applicationDate: string | null;
};

/** Транспортные документы / ТН, привязанные к маршрутам перевозчика. */
export type CarrierTnRow = {
  key: string;
  routeId: number;
  routeNumber: string;
  tnId: number | null;
  label: string;
};

const BATCH = 20;
const APPS_BATCH = 25;

function pickClientFromView(
  view: RouteViewModel | null
): { id: number | null; name: string } {
  if (!view) return { id: null, name: ENTITY_CARD_DASH };
  const c = view.parties?.client as { id?: unknown; name?: string | null } | null;
  if (c) {
    const name = c.name && String(c.name).trim() ? String(c.name).trim() : '';
    const rawId = c.id;
    const id = typeof rawId === 'number' && Number.isFinite(rawId) ? rawId : null;
    if (name) return { id, name };
  }
  const apps = view.applications;
  if (Array.isArray(apps) && apps.length > 0) {
    const app0 = apps[0] as { application?: { client?: { id?: number; name?: string } } };
    const cl = app0?.application?.client;
    if (cl?.id != null && cl.name && String(cl.name).trim()) {
      return { id: cl.id, name: String(cl.name).trim() };
    }
  }
  return { id: null, name: ENTITY_CARD_DASH };
}

function viewOperationLoadUnloadDisplays(view: RouteViewModel | null): { load: string; unl: string } {
  if (!view) return { load: ENTITY_CARD_DASH, unl: ENTITY_CARD_DASH };
  const d = view.operation.dates;
  const loadRaw = d.actualLoading || d.plannedLoading;
  const unlRaw = d.actualUnloading || d.plannedUnloading;
  return { load: formatDate(loadRaw), unl: formatDate(unlRaw) };
}

/**
 * Маршруты перевозчика через GET /routes?carrierId=…, затем GET /routes/:id/view (routePenalties + operation/parties).
 */
export function useCarrierPenalties(carrierId: number | undefined, enabled: boolean) {
  const dispatch = useDispatch<AppDispatch>();

  const { data: routesResp, isLoading: routesLoading, isError: routesError } = useGetAllRoutesQuery(
    { carrierId: carrierId!, page: 1, limit: 1000 },
    { skip: !enabled || !carrierId }
  );

  const [routeRows, setRouteRows] = useState<CarrierRoutePenaltiesRow[]>([]);
  const [flatRows, setFlatRows] = useState<CarrierPenaltyTableRow[]>([]);
  const [applicationRows, setApplicationRows] = useState<CarrierApplicationRow[]>([]);
  const [tnRows, setTnRows] = useState<CarrierTnRow[]>([]);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [viewsError, setViewsError] = useState<string | null>(null);

  const routeIdsKey = useMemo(() => (routesResp?.routes?.map((r) => r.id).join(',') ?? ''), [routesResp?.routes]);

  useEffect(() => {
    if (!enabled || !carrierId) {
      setRouteRows([]);
      setFlatRows([]);
      setApplicationRows([]);
      setTnRows([]);
      setViewsLoading(false);
      setViewsError(null);
      return;
    }
    if (routesLoading || routesResp == null) {
      return;
    }
    const routeList = routesResp.routes ?? [];
    if (routeList.length === 0) {
      setRouteRows([]);
      setFlatRows([]);
      setApplicationRows([]);
      setTnRows([]);
      setViewsLoading(false);
      setViewsError(null);
      return;
    }

    let cancelled = false;
    setViewsLoading(true);
    setViewsError(null);
    setRouteRows([]);
    setFlatRows([]);
    setApplicationRows([]);
    setTnRows([]);

    const idToNumber = new Map<number, string>(
      routeList.map((r: Route) => [r.id, (r.number && String(r.number).trim()) || String(r.id)])
    );
    const ids = routeList.map((r: Route) => r.id);

    (async () => {
      try {
        const views: RouteViewModel[] = [];
        for (let i = 0; i < ids.length; i += BATCH) {
          if (cancelled) return;
          const chunk = ids.slice(i, i + BATCH);
          const batchResults = await Promise.all(
            chunk.map(async (id) => {
              const result = await dispatch(routesApi.endpoints.getRouteView.initiate(id));
              const data = (result as { data?: RouteViewModel }).data;
              return data ?? null;
            })
          );
          for (const v of batchResults) {
            if (v) views.push(v);
          }
        }
        if (cancelled) return;

        const viewById = new Map<number, RouteViewModel>(views.map((v) => [v.operation.id, v]));

        const flat: CarrierPenaltyTableRow[] = [];
        const perRoute: CarrierRoutePenaltiesRow[] = [];

        for (const route of routeList) {
          const routeId = route.id;
          const routeNumber = idToNumber.get(routeId) ?? String(routeId);
          const view = viewById.get(routeId) ?? null;

          const directionLabel = view?.operation?.direction?.label?.trim() || 'Направление не указано';
          const { load: loadingDateDisplay, unl: unloadingDateDisplay } = viewOperationLoadUnloadDisplays(view);
          const { id: clientId, name: clientName } = pickClientFromView(view);
          const op = view?.operation;
          const isCompanyRoute = op?.isCompanyRoute ?? false;
          const ccr = op?.carrierContractRequest;
          const carrierContractRequestNumber =
            ccr?.number != null && String(ccr.number).trim() !== '' ? String(ccr.number).trim() : null;
          const carrierContractRequestDate =
            ccr?.date != null && String(ccr.date).trim() !== '' ? String(ccr.date) : null;

          const penalties: CarrierPenaltyTableRow[] = [];
          const raw = view?.routePenalties ?? [];
          for (const p of raw) {
            const row: CarrierPenaltyTableRow = {
              key: `${routeId}-${p.id}`,
              routeId,
              routeNumber,
              reason: p.agreementPenalty?.name?.trim() || 'Без названия',
              amount: Number(p.calculatedAmount) || 0,
              status: p.status
            };
            penalties.push(row);
            flat.push(row);
          }

          const penaltySum = penalties.reduce((s, x) => s + x.amount, 0);

          perRoute.push({
            key: String(routeId),
            routeId,
            routeNumber,
            directionLabel,
            loadingDateDisplay,
            unloadingDateDisplay,
            clientName,
            clientId,
            isCompanyRoute,
            carrierContractRequestNumber,
            carrierContractRequestDate,
            routeStatus: String(route.status ?? ''),
            penalties,
            penaltyCount: penalties.length,
            penaltySum
          });
        }

        perRoute.sort((a, b) => b.routeId - a.routeId);

        const appMap = new Map<
          number,
          { applicationId: number; internalNumber: string; applicationDate: string | null }
        >();
        const tnMap = new Map<string, CarrierTnRow>();

        for (const v of views) {
          const rid = v.operation.id;
          const rnum = idToNumber.get(rid) ?? String(rid);
          for (const s of v.operation.linkedApplicationsSummary ?? []) {
            const aid = s.applicationId;
            if (!appMap.has(aid)) {
              const rawNum = s.internalNumber != null && String(s.internalNumber).trim() !== '' ? String(s.internalNumber).trim() : String(aid);
              const ad = s.applicationDate;
              const applicationDate =
                ad != null && String(ad).trim() !== '' ? String(ad) : null;
              appMap.set(aid, {
                applicationId: aid,
                internalNumber: rawNum,
                applicationDate
              });
            }
          }

          const transportDocs = (v.documents?.raw?.transportDocuments ?? []) as Record<string, unknown>[];
          for (const d of transportDocs) {
            const tnId =
              typeof d.transportConsignmentNoteId === 'number'
                ? d.transportConsignmentNoteId
                : typeof d.tnId === 'number'
                  ? d.tnId
                  : null;
            const docType = String(d.documentType || '').toUpperCase();
            const isTn =
              tnId != null ||
              docType.includes('TN') ||
              docType.includes('TRANSPORT') ||
              docType === 'ORIGINAL_TN';
            if (!isTn) continue;
            const num = d.number ?? d.documentNumber ?? '';
            const key =
              tnId != null ? `tn-${tnId}` : `doc-${rid}-${String(d.id ?? Math.random())}`;
            if (!tnMap.has(key)) {
              tnMap.set(key, {
                key,
                routeId: rid,
                routeNumber: rnum,
                tnId,
                label: String(
                  num ||
                    (tnId != null ? `Транспортная накладная №${tnId}` : 'Транспортный документ')
                )
              });
            }
          }
        }

        const appIds = Array.from(appMap.keys());
        const appById = new Map<number, Application>();
        for (let j = 0; j < appIds.length; j += APPS_BATCH) {
          if (cancelled) return;
          const chunk = appIds.slice(j, j + APPS_BATCH);
          await Promise.all(
            chunk.map(async (aid) => {
              try {
                const app = await dispatch(
                  applicationsApi.endpoints.getApplicationById.initiate(aid)
                ).unwrap();
                if (app?.id) appById.set(app.id, app);
              } catch {
                /* нет заявки или сеть */
              }
            })
          );
        }
        if (cancelled) return;

        const applicationRowsBuilt: CarrierApplicationRow[] = Array.from(appMap.values())
          .sort((a, b) => b.applicationId - a.applicationId)
          .map((r) => ({
            key: String(r.applicationId),
            applicationId: r.applicationId,
            application: appById.get(r.applicationId) ?? null,
            internalNumber: r.internalNumber,
            applicationDate: r.applicationDate
          }));

        setRouteRows(perRoute);
        setFlatRows(flat);
        setApplicationRows(applicationRowsBuilt);
        setTnRows(Array.from(tnMap.values()).sort((a, b) => b.routeId - a.routeId));
      } catch (e) {
        if (!cancelled) {
          setViewsError(e instanceof Error ? e.message : 'Не удалось загрузить штрафы');
          setRouteRows([]);
          setFlatRows([]);
          setApplicationRows([]);
          setTnRows([]);
        }
      } finally {
        if (!cancelled) setViewsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [carrierId, enabled, dispatch, routesResp, routesLoading, routeIdsKey]);

  const { totalRub, penaltyCount } = useMemo(() => {
    const total = flatRows.reduce((s, r) => s + r.amount, 0);
    return { totalRub: total, penaltyCount: flatRows.length };
  }, [flatRows]);

  const loading = routesLoading || viewsLoading;
  const error = routesError ? 'Не удалось загрузить маршруты' : viewsError;

  return {
    routeRows,
    rows: flatRows,
    applicationRows,
    tnRows,
    totalRub,
    penaltyCount,
    loading,
    error,
    routesCount: routesResp?.routes?.length ?? 0
  };
}
